import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Scrollable from "../../Shared/Edit/Scrollable";
import ComponentToolbar from "./ComponentToolbar/ComponentToolbar";
import { useSelectedPage, useUpdatePageComponent } from "../../../Hooks/Display/usePages";
import { useSelectedComponent } from "../../../Hooks/Display/useComponents";
import { usePropsStoreValue } from "../../../Hooks/PropStore/usePropsStore";
import { useChangeBooleans, useComponentProperties, useFetchedValues, useResetComponentProperties, useSaveComponent } from "../../../Hooks/Display/useComponentProperties";
import { ATTRIBUTE_LABEL } from "../../Helpers/Labels/AttributeLabel";
import componentPropertiesMap from './ComponentFormProperties.json';
import { useEffect, useState, useContext } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { getAllAttributes } from "../../Helpers/Schema/Schema";
import ComponentFormSkeleton from "../../Display/Shared/ComponentFormSkeleton";
import { useTranslation } from "react-i18next";
import { getAttributes } from "../../../API/Attribute/Attribute";
import { isLanguagesChanged } from "../utils";
import GenericTranslationDialog from "../../Entities/GenericTranslationDialog";
import { GlobalContext } from "../../../Renderers/GlobalContext";

function ProgressBar() {

    const labelOrientations = ["Left", "Center", "Right"];
    const types = ["Progressive", "Discrete"];
    const { subscription } = useContext(GlobalContext).state
    const formProperties = componentPropertiesMap["Progress Bar"];

    // hooks
    const selectedPage = useSelectedPage();
    const selectedComponent = useSelectedComponent();
    const accessedFromDesigner = usePropsStoreValue("accessedFromDesigner");
    const entityId = usePropsStoreValue("entityId");
    const project = usePropsStoreValue("project");
    const resetContext = useResetComponentProperties();
    const saveComponent = useSaveComponent();
    const updatePageComponent = useUpdatePageComponent(selectedPage.parentId, selectedPage._id);

    const [properties, setProperties] = useComponentProperties()
    const [{ generalProperties: fetchedValues }, { updateGeneralFetchedValue }] = useFetchedValues();
    const [{ isGeneralPropertiesChanged }, setChangeProperty] = useChangeBooleans();
    const { t, i18n } = useTranslation([
        "display/common",
        "display/component_toolbar",
        "display/components/progress_bar",
        "attribute/property_panel"
    ], { useSuspense: false });

    const [loading, setLoading] = useState(true);
    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    useEffect(() => {
        getData();
    }, [])

    // componentWillUnmount
    useEffect(() => {
        return () => {
            resetContext();
        }
    }, [])

    useEffect(() => {
        if (!loading) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
    }, [selectedComponent, selectedPage]);

    const [initialisationDone, setInitialisationDone] = useState(false);
    useEffect(() => {
        if (initialisationDone) {
            setIsChanged();
        }
    }, [properties])

    // state
    const [tabSelected, setTabSelected] = useState(0);

    // functions
    async function getData() {
        const attributes = await getAttributes(entityId);
        if (selectedPage && selectedComponent) {
            setPropertiesUsingAttributes(attributes);
            setChangeProperty("isGeneralPropertiesChanged", false);
            const filteredAttributes = attributes.filter((attr) =>
                attr.attributeType === "Mirror" ? attr.attribute.attributeType === "State Field" : attr.attributeType === "State Field"
            );
            updateGeneralFetchedValue("attributes", filteredAttributes);
            setInitialisationDone(true);
            setLoading(false);
        }
    }

    // handlers
    function setPropertiesUsingAttributes(attributes) {
        for (const property of formProperties) {
            switch (property) {
                case "statusAttributeId":
                    const attribute = attributes.find(attribute => attribute._id === selectedComponent[property]);
                    if (attribute) {
                        setProperties("statusAttribute", attribute);
                    }
                    break;
                case "languages":
                    setProperties("languages", selectedComponent[property] || {});
                    break;
                default:
                    if (
                        selectedComponent[property] !== undefined
                    ) {
                        setProperties(property, selectedComponent[property])
                    }
                    break;
            }
        }
    }

    function handleTabSelect(e) {
        setTabSelected(e.selected);
    }

    function handleInputChange(e) {
        setProperties(e.target.name, e.target.value);
    }

    function setLabelTranslation(data) {
        setProperties("languages", data);
        setShowLabelTranslationDialog(false);
    }

    function setIsChanged() {
        let isChanged = false;
        for (const property of formProperties) {
            switch (property) {
                case "statusAttributeId":
                    if (
                        properties.statusAttribute &&
                        selectedComponent[property] !== properties.statusAttribute._id
                    ) {
                        isChanged = true;
                    }
                    break;
                case "languages":
                    if (properties.languages && isLanguagesChanged(properties.languages, selectedComponent.languages)) {
                        isChanged = true;
                    }
                    break;
                default:
                    if (selectedComponent[property] !== properties[property]) {
                        isChanged = true;
                    }
                    break;
            }
            if (isChanged) {
                break;
            }
        }
        setChangeProperty("isGeneralPropertiesChanged", isChanged);
    }

    async function handleSave() {
        const updatedComponent = await saveComponent();
        updatePageComponent(selectedComponent.id, updatedComponent);
    }

    function resetComponent() {
        if (isGeneralPropertiesChanged) {
            setPropertiesUsingAttributes(fetchedValues.attributes);
        }
    }

    if (loading) {
        return (<ComponentFormSkeleton />);
    }

    return (
        <div className={"h-100 d-flex flex-column line" + (!accessedFromDesigner && " runtimePopup")}>
            <ComponentToolbar onSave={handleSave} onReset={resetComponent} />
            <Scrollable noPadding={!accessedFromDesigner}>
                <TabStrip
                    className="flex-grow-1 h-100 edit1"
                    selected={tabSelected}
                    onSelect={handleTabSelect}
                >
                    <TabStripTab title={t("tabs.general", { ns: "display/common" })}>
                        <form className="p-2 ms-2">
                            <div className="form-label">
                                {t("form_labels.status_property", { ns: "display/components/progress_bar" })}
                                {
                                    properties.AttributeList &&
                                    properties.AttributeList.length === 0 &&
                                    properties.showReasonForNotEnabling &&
                                    (
                                        <span className="text-danger">
                                            &nbsp;
                                            {t("errors.no_eligible_attribute", { ns: "display/components/progress_bar" })}
                                        </span>
                                    )}
                            </div>
                            <DropDownList
                                name="statusAttribute"
                                disabled={selectedComponent.hidden}
                                onChange={handleInputChange}
                                className="form-control-custom w-25"
                                value={properties.statusAttribute}
                                data={fetchedValues.attributes}
                                textField="attributeLabel"
                            />
                            <div className="form-label">
                                {t("form_labels.label", { ns: "display/components/progress_bar" })}
                            </div>
                            <input
                                disabled={selectedComponent.hidden}
                                placeholder={t("placeholders.label", { ns: "display/components/progress_bar" })}
                                className="form-control w-50"
                                name="label"
                                maxLength={256}
                                onChange={handleInputChange}
                                value={properties.label}
                            />
                            {subscription.numOfLanguages > 1 &&
                                <div className="col-12">
                                    <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                                        setShowLabelTranslationDialog(true)
                                    }}>
                                        <span className="text-primary">
                                            {t("form_labels.label_translation")}
                                        </span>
                                    </div>
                                </div>
                            }
                            {showLabelTranslationDialog && (
                                <GenericTranslationDialog
                                    onClose={() => { setShowLabelTranslationDialog(false) }}
                                    onSave={setLabelTranslation}
                                    languages={properties.languages}
                                    project={project}
                                    fields={["label"]}
                                />
                            )}
                            {accessedFromDesigner && (
                                <>
                                    {/* <div className="form-label">Style</div>
                                <DropDownList
                                disabled={selectedComponent.hidden}
                                onChange={handleStyle}
                                className="form-control-custom w-25"
                                value={properties.style}
                                data={Styles}
                            /> */}
                                    <div className="form-label">
                                        {t("form_labels.label_alignment", { ns: "display/components/progress_bar" })}
                                    </div>
                                    <DropDownList
                                        name="labelOrientation"
                                        disabled={selectedComponent.hidden}
                                        onChange={handleInputChange}
                                        className="form-control-custom w-25"
                                        value={properties.labelOrientation}
                                        data={labelOrientations}
                                    />
                                </>
                            )}
                            <div className="form-label">
                                {t("form_labels.type", { ns: "display/components/progress_bar" })}
                            </div>
                            <DropDownList
                                name="type"
                                disabled={selectedComponent.hidden}
                                onChange={handleInputChange}
                                className="form-control-custom w-25"
                                value={properties.type}
                                data={types}
                            />
                        </form>
                    </TabStripTab>
                </TabStrip>
            </Scrollable>
        </div>
    )
}

export default ProgressBar;